import { Color } from "@repo/app-types/constants/colors";
import {
  defaultOutcomeDefinitions,
  funnelOutcomeDefinitionId,
  funnelOutcomeDefinitionName,
  getOutcomeLabelsMissingFromContext,
  outcomeGreenRedYellowResultCategoryColor,
  revenueOutcomeDefinitionId,
  revenueOutcomeDefinitionName,
  revenueOutcomeResults,
  type OutcomeDefinition,
  type OutcomeGreenRedYellowResultCategory,
} from "@repo/app-types/entities/outcome";
import type { PlaybookId } from "@repo/app-types/entities/playbook";
import type { PlaybookNodeSampleToolResultOutcomePayload } from "@repo/app-types/entities/playbookNodeSampleToolResult";
import { FeatureFlagKey } from "@repo/app-types/featureFlags/keys";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { Size } from "@repo/ui/helpers/helpers";
import { Label } from "@repo/ui/Label/label";
import { Pill, getPillColor, type PillColor } from "@repo/ui/Pills/pill";
import {
  SimpleSelect,
  type SimpleSelectOption,
} from "@repo/ui/Select/simpleSelect";
import {
  IconAlertTriangle,
  IconPencil,
  IconSparkles,
} from "@tabler/icons-react";
import { useMemo, useState, type FC } from "react";
import { twMerge } from "tailwind-merge";
import { useIsFeatureFlagEnabled } from "../../helpers/featureFlags/hooks.js";
import type { SampleToolResultOutcomeEntry } from "../../hooks/useSampleToolResults.js";
import { OutcomeFields } from "./outcomeFields.js";
import { OutcomeLabelsEditorSlideoverContainer } from "./outcomeLabelsEditorSlideoverContainer.js";
import { OutcomeSuggestionResults } from "./outcomeSuggestionResults.js";

export interface OutcomeSettingsFormProps {
  outcome: OutcomeDefinition | null;
  initialOutcome: OutcomeDefinition | null;
  /**
   * Live/published outcome. Used as the diff baseline for the context
   * "See changes" toggle in non-suggestion mode.
   */
  liveOutcome?: OutcomeDefinition | null;
  onUpdateOutcome: (outcome: OutcomeDefinition | null) => void;
  isReadOnly: boolean;
  /**
   * The agent's saved sample-tool result for the outcome, if any. When the
   * candidate context differs from the current outcome context, the form
   * renders in suggestion mode: banner + read-only form + Results pane.
   */
  suggestion?: SampleToolResultOutcomeEntry;
  /** Apply the agent's proposed context. Called by the suggestion banner. */
  onApplySuggestion?: (finalOutcome: OutcomeDefinition) => void;
  /** Drop the saved suggestion for the outcome (used by Apply & Discard). */
  onDiscardSuggestion?: () => void;
  /** Ask the AI assistant to optimize the outcome rubric (its context). */
  onOptimizeOutcome?: (outcome: OutcomeDefinition) => void;
  /** Ask the AI assistant to run the sample-distribution tool against the outcome as-is. */
  onTestOutcome?: (outcome: OutcomeDefinition) => void;
  /** Whether an assistant run is in progress (gates Optimize/Test). */
  isOptimizingSectionOrCriterion?: boolean;
  playbookId?: PlaybookId | null;
  /**
   * Fill a bounded parent (the assessment editor's settings pane), letting the
   * context field scroll internally. Pass `false` on a flowing page so the form
   * grows with its content and the page is the only scroll region.
   * @default true
   */
  fillsHeight?: boolean;
}

export const OutcomeSettingsForm: FC<OutcomeSettingsFormProps> = ({
  outcome,
  initialOutcome,
  liveOutcome,
  onUpdateOutcome,
  isReadOnly,
  suggestion,
  onApplySuggestion,
  onDiscardSuggestion,
  onOptimizeOutcome,
  onTestOutcome,
  isOptimizingSectionOrCriterion = false,
  playbookId,
  fillsHeight = true,
}) => {
  // Field-backed outcomes need more setup before they are valid enough to
  // commit. Keep the selected type pending locally while the user chooses its
  // custom-field settings, without overwriting the current saved outcome.
  const [pendingOutcomeId, setPendingOutcomeId] = useState<string | null>(null);
  const [isEditingLabels, setIsEditingLabels] = useState(false);

  const isSuggestionMode =
    suggestion !== undefined &&
    outcome !== null &&
    isOutcomeInSuggestionMode(outcome, suggestion);

  const displayOutcome =
    isSuggestionMode && outcome
      ? mergeSuggestionIntoOutcome(outcome, suggestion.candidate_draft)
      : outcome;

  // Diff baseline for the context "See changes" toggle. In suggestion mode the
  // diff shows current → proposed (baseline = the current outcome); otherwise it
  // shows the unpublished draft vs the live/published outcome.
  const diffBaselineOutcome = isSuggestionMode
    ? outcome
    : (liveOutcome ?? null);

  // In suggestion mode the form is a read-only preview of the proposed state.
  const effectiveReadOnly = isReadOnly || isSuggestionMode;

  const activeOutcomeId = pendingOutcomeId ?? displayOutcome?.id;
  const isRevenueOutcomeActive = activeOutcomeId === revenueOutcomeDefinitionId;
  const isFunnelOutcomeActive = activeOutcomeId === funnelOutcomeDefinitionId;

  const isFunnelOutcomeFlagEnabled = useIsFeatureFlagEnabled(
    FeatureFlagKey.FunnelOutcome,
  );
  // Surface the Funnel option whenever the flag is on, or when the playbook
  // already has a Funnel outcome saved (so existing setups remain editable
  // even if the flag is later disabled).
  const showFunnelOption =
    isFunnelOutcomeFlagEnabled ||
    initialOutcome?.id === funnelOutcomeDefinitionId;

  // Field-backed outcomes are not part of defaultOutcomeDefinitions because
  // they require custom-field settings to be valid, so add them manually.
  const outcomeOptions: SimpleSelectOption<string | null>[] = useMemo(() => {
    return [
      {
        value: null,
        label: "None",
      },
      ...defaultOutcomeDefinitions.map((def) => ({
        value: def.id,
        label: def.name,
      })),
      {
        value: revenueOutcomeDefinitionId,
        label: revenueOutcomeDefinitionName,
      },
      ...(showFunnelOption
        ? [
            {
              value: funnelOutcomeDefinitionId,
              label: funnelOutcomeDefinitionName,
            },
          ]
        : []),
    ];
  }, [showFunnelOption]);

  const selectedOutcomeValue: string | null = activeOutcomeId ?? null;

  const handleOutcomeSelect = (selectedValue: string | null) => {
    if (!selectedValue) {
      setPendingOutcomeId(null);
      onUpdateOutcome(null);
      return;
    }

    // If selecting the initial outcome from the playbook, restore it
    if (initialOutcome && initialOutcome.id === selectedValue) {
      setPendingOutcomeId(null);
      onUpdateOutcome(initialOutcome);
      return;
    }

    if (selectedValue === revenueOutcomeDefinitionId) {
      // Don't overwrite the committed outcome yet — the user still needs to
      // pick a custom field before we have a valid revenue outcome. Keep any
      // previously committed outcome in place until the field is chosen.
      setPendingOutcomeId(
        outcome?.id === revenueOutcomeDefinitionId ? null : selectedValue,
      );
      return;
    }

    if (selectedValue === funnelOutcomeDefinitionId) {
      setPendingOutcomeId(
        outcome?.id === funnelOutcomeDefinitionId ? null : selectedValue,
      );
      return;
    }

    setPendingOutcomeId(null);
    const defaultOutcome = defaultOutcomeDefinitions.find(
      (def) => def.id === selectedValue,
    );
    if (defaultOutcome) {
      onUpdateOutcome(defaultOutcome);
    }
  };

  const getCategoryPillColor = (
    category: OutcomeGreenRedYellowResultCategory,
  ): PillColor => {
    const color = outcomeGreenRedYellowResultCategoryColor[category];
    return getPillColor(color) ?? Color.Gray;
  };

  // Pills to render as the outcome's Values. We show the committed outcome's
  // own `results` so customized labels render live after editing. The static
  // revenue tuple is only used while Revenue is *pending* selection (the
  // backing field isn't chosen yet, so `outcome` still holds the previously
  // committed, non-revenue outcome). Funnel result pills are owned by the
  // funnel field editor, so we suppress the static list here.
  const isPendingRevenueSelection =
    isRevenueOutcomeActive && outcome?.id !== revenueOutcomeDefinitionId;
  const resultsToDisplay:
    | readonly {
        category: OutcomeGreenRedYellowResultCategory;
        label: string;
      }[]
    | null = isFunnelOutcomeActive
    ? null
    : isPendingRevenueSelection
      ? revenueOutcomeResults
      : (outcome?.results ?? null);

  // The pencil edits the committed outcome's labels. Hide it while a
  // field-backed outcome is pending (no committed outcome to edit yet) and for
  // Funnel, whose labels come from its stage editor. Also hidden while
  // previewing an agent suggestion (read-only).
  const canEditLabels =
    !effectiveReadOnly &&
    pendingOutcomeId === null &&
    !!outcome &&
    outcome.id !== funnelOutcomeDefinitionId;

  // Nudge the user when the context no longer mentions every customizable
  // outcome label (e.g. after a rename). Only non-field-backed outcomes have a
  // context field, so skip revenue and funnel. Suppressed while previewing a
  // suggestion (the displayed context is the proposed one, not the committed).
  const showContextWarning =
    !effectiveReadOnly &&
    !!outcome &&
    !isRevenueOutcomeActive &&
    !isFunnelOutcomeActive &&
    getOutcomeLabelsMissingFromContext(outcome).length > 0;

  const formBody = (
    <div
      className={twMerge(
        "flex flex-col space-y-4 p-2",
        fillsHeight && "min-h-0 flex-1",
      )}
    >
      {showContextWarning ? (
        <div className="flex items-start gap-3 rounded-md border border-amber-300 bg-amber-50 p-4">
          <IconAlertTriangle
            aria-hidden
            className="mt-0.5 size-5 shrink-0 text-amber-500"
          />
          <div>
            <h3 className="text-sm font-medium text-amber-800">
              Values not found in context
            </h3>
            <p className="mt-1 text-sm text-amber-700">
              Review the context below and make sure it explains when the agent
              should choose each outcome value.
            </p>
          </div>
        </div>
      ) : null}

      <div>
        <SimpleSelect
          label="Type"
          options={outcomeOptions}
          value={selectedOutcomeValue}
          onChange={handleOutcomeSelect}
          isDisabled={effectiveReadOnly}
        />
      </div>

      {resultsToDisplay ? (
        <div>
          <Label size={Size.SMALL}>Values</Label>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {resultsToDisplay.map((result, index) => (
              <Pill
                key={index}
                label={result.label}
                color={getCategoryPillColor(result.category)}
                size={Size.SMALL}
              />
            ))}
            {canEditLabels ? (
              <IconButton
                variant={ButtonVariant.Tertiary}
                icon={<IconPencil className="size-4" />}
                onClick={() => setIsEditingLabels(true)}
                accessibleLabel="Edit labels"
              />
            ) : null}
          </div>
        </div>
      ) : null}

      {outcome ? (
        <OutcomeLabelsEditorSlideoverContainer
          outcome={outcome}
          isOpen={isEditingLabels}
          onClose={() => setIsEditingLabels(false)}
          onSave={(updatedOutcome) => {
            onUpdateOutcome(updatedOutcome);
            setIsEditingLabels(false);
          }}
        />
      ) : null}

      <OutcomeFields
        outcome={displayOutcome}
        diffBaselineContext={diffBaselineOutcome?.context ?? null}
        isRevenueOutcomeActive={isRevenueOutcomeActive}
        isFunnelOutcomeActive={isFunnelOutcomeActive}
        onUpdateOutcome={(updatedOutcome) => {
          setPendingOutcomeId(null);
          onUpdateOutcome(updatedOutcome);
        }}
        isReadOnly={effectiveReadOnly}
        fillsHeight={fillsHeight}
      />
    </div>
  );

  // Optimize/Test only make sense for a rubric-graded outcome — the context
  // textarea (and thus a testable rubric) exists only for non-revenue,
  // non-funnel outcomes, matching what `OutcomeFields` renders.
  const showOptimizeTestFooter =
    !isReadOnly &&
    displayOutcome !== null &&
    !isRevenueOutcomeActive &&
    !isFunnelOutcomeActive &&
    (onOptimizeOutcome !== undefined || onTestOutcome !== undefined);

  if (!isSuggestionMode) {
    const footer =
      showOptimizeTestFooter && displayOutcome ? (
        <div className="mt-auto flex items-center justify-end gap-2 px-2 pt-4 pb-2">
          {onOptimizeOutcome ? (
            <Button
              variant={ButtonVariant.Secondary}
              size={Size.MEDIUM}
              icon={<IconSparkles className="size-4" />}
              onClick={() => onOptimizeOutcome(displayOutcome)}
              isDisabled={isOptimizingSectionOrCriterion}
            >
              Optimize
            </Button>
          ) : null}
          {onTestOutcome ? (
            <Button
              variant={ButtonVariant.Primary}
              size={Size.MEDIUM}
              onClick={() => onTestOutcome(displayOutcome)}
              isDisabled={isOptimizingSectionOrCriterion}
            >
              Test
            </Button>
          ) : null}
        </div>
      ) : null;

    // A check-only Test produced a result without a proposed change: show the
    // editable form + footer on the left and the Results pane on the right (no
    // suggestion banner). The explicit `suggestion !== undefined` check narrows
    // the type for `suggestion.aggregates` below.
    if (suggestion !== undefined && displayOutcome) {
      return (
        <div className="flex h-full min-h-0 flex-col">
          <div className="grid min-h-0 flex-1 grid-cols-1 gap-x-2 md:grid-cols-[1fr_1px_1fr]">
            <div className="flex min-h-0 flex-1 flex-col">
              <div className="flex min-h-0 flex-1 flex-col">{formBody}</div>
              {footer}
            </div>
            <div
              aria-hidden
              className="-mt-2 -mb-2 hidden bg-gray-200 md:block"
            />
            <div className="flex min-h-0 flex-col gap-4 p-2">
              <h3 className="shrink-0 text-base font-semibold text-foreground">
                Results
              </h3>
              <div className="min-h-0 flex-1 overflow-y-auto p-1">
                <OutcomeSuggestionResults
                  aggregates={suggestion.aggregates}
                  suggestion={suggestion}
                  outcomeDefinition={displayOutcome}
                  playbookId={playbookId}
                />
              </div>
            </div>
          </div>
        </div>
      );
    }

    // No saved result for this outcome: single-column form, no Results pane.
    return (
      <div className={twMerge("flex flex-col", fillsHeight && "min-h-full")}>
        <div className="flex min-h-0 flex-1 flex-col">{formBody}</div>
        {footer}
      </div>
    );
  }

  // Suggestion mode: banner + read-only proposed form on the left, Results on
  // the right. `outcome` and `displayOutcome` are non-null here.
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="-mx-2 -mt-2 flex shrink-0 items-center justify-between rounded-t-lg bg-indigo-50 px-3 py-2">
        <div className="flex items-center gap-2 text-sm text-indigo-900">
          <IconSparkles className="size-4 shrink-0 text-indigo-500" />
          <span>Showing changes suggested by your agent.</span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={ButtonVariant.Tertiary}
            size={Size.MEDIUM}
            onClick={() => onDiscardSuggestion?.()}
          >
            Discard changes
          </Button>
          <Button
            variant={ButtonVariant.Primary}
            size={Size.MEDIUM}
            onClick={() => {
              if (displayOutcome) onApplySuggestion?.(displayOutcome);
            }}
          >
            Apply changes
          </Button>
        </div>
      </div>
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-x-2 md:grid-cols-[1fr_1px_1fr]">
        <div className="flex min-h-0 flex-1 flex-col">{formBody}</div>
        <div aria-hidden className="-mb-2 hidden bg-gray-200 md:block" />
        <div className="flex min-h-0 flex-col gap-4 p-2">
          <h3 className="shrink-0 text-base font-semibold text-foreground">
            Results
          </h3>
          <div className="min-h-0 flex-1 overflow-y-auto p-1">
            {displayOutcome ? (
              <OutcomeSuggestionResults
                aggregates={suggestion.aggregates}
                suggestion={suggestion}
                outcomeDefinition={displayOutcome}
                playbookId={playbookId}
              />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * For an outcome suggestion, merge the proposed `context` into the current
 * outcome. Only `context` is ever changed — the type and result values/labels
 * are preserved verbatim.
 */
export function mergeSuggestionIntoOutcome(
  current: OutcomeDefinition,
  candidateDraft: PlaybookNodeSampleToolResultOutcomePayload["candidate_draft"],
): OutcomeDefinition {
  return {
    ...current,
    ...(candidateDraft.context !== undefined
      ? { context: candidateDraft.context }
      : {}),
  };
}

/**
 * True when the outcome should render with the suggestion banner + read-only
 * form + Results pane. A "check-only" run — where the candidate context equals
 * the current outcome context — does not count.
 */
export function isOutcomeInSuggestionMode(
  outcome: OutcomeDefinition,
  suggestion: SampleToolResultOutcomeEntry | undefined,
): boolean {
  if (!suggestion) return false;
  const candidateContext = suggestion.candidate_draft.context;
  if (candidateContext === undefined) return false;
  return candidateContext !== outcome.context;
}
